import { apolloClient } from '../../../App';
import { DeliveryFragmentFragmentDoc, GetDeliveriesDocument, DeliveryFragmentFragment } from 'data/__generated__';

// Les crons créent les livraisons une par une (`delivery.repository.ts` `create()` publie un évènement
// par document), donc une rafale de créations produit une rafale d'évènements. On ne déclenche qu'un
// seul refetch, une seconde après la fin de la rafale.
const REFETCH_DEBOUNCE_MS = 1000;
let pendingRefetch: ReturnType<typeof setTimeout> | null = null;

function scheduleDeliveriesRefetch(): void {
    if (pendingRefetch) {
        clearTimeout(pendingRefetch);
    }
    pendingRefetch = setTimeout(() => {
        pendingRefetch = null;
        apolloClient.refetchQueries({ include: [GetDeliveriesDocument] });
    }, REFETCH_DEBOUNCE_MS);
}

/**
 * Écrit une livraison sur son entité normalisée. Toutes les listes qui la référencent — quelles que
 * soient leurs variables — se mettent à jour d'elles-mêmes.
 *
 * Renvoie `false` si la livraison n'était pas encore en cache. Il ne faut PAS l'y insérer à la main :
 * la souscription ne transporte qu'un `DeliveryFragment`, alors que `GET_DELIVERIES` sélectionne aussi
 * `contentSummary` et `soldoutRefsCount`. Une entité partielle rendrait la lecture de toute la query
 * incomplète (`data` devient `undefined`) — l'appelant refetch à la place.
 */
function writeDeliveryToCache(updatedDelivery: DeliveryFragmentFragment): boolean {
    const cacheId = apolloClient.cache.identify({ __typename: 'Delivery', _id: updatedDelivery._id });
    if (!cacheId) {
        return false;
    }

    const cachedDelivery = apolloClient.readFragment<DeliveryFragmentFragment>({
        id: cacheId,
        fragment: DeliveryFragmentFragmentDoc,
        fragmentName: 'DeliveryFragment',
    });
    if (!cachedDelivery) {
        return false;
    }

    // Un évènement peut arriver après la réponse de la mutation qui l'a déclenché (et la même mise à
    // jour est parfois reçue plusieurs fois) : sans ce garde-fou, un évènement en retard réécrase la
    // livraison avec un état plus ancien.
    if (cachedDelivery.updatedAt > updatedDelivery.updatedAt) {
        return true;
    }

    apolloClient.writeFragment({
        id: cacheId,
        fragment: DeliveryFragmentFragmentDoc,
        fragmentName: 'DeliveryFragment',
        data: updatedDelivery,
    });

    return true;
}

export const deliveriesUpdatedHandler = (updatedDeliveries: DeliveryFragmentFragment[]) => {
    // `map` et non `some` : toutes les livraisons connues doivent être écrites, même si l'une d'elles
    // impose déjà un refetch.
    const hasUnknownDelivery = updatedDeliveries.map(writeDeliveryToCache).includes(false);

    if (hasUnknownDelivery) {
        scheduleDeliveriesRefetch();
    }
};

export function deliveryUpdatedHandler(deliveryId: string, updatedDeliveries: DeliveryFragmentFragment[]) {
    const updatedDelivery = updatedDeliveries.find(({ _id }) => _id === deliveryId);
    if (!updatedDelivery) {
        return;
    }

    writeDeliveryToCache(updatedDelivery);
}
